import { Fragment, useCallback, useEffect, useMemo, useState } from 'react'
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Divider,
  LinearProgress,
  MenuItem,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
  alpha,
  useTheme,
} from '@mui/material'
import { ChartEmpty } from '../components/ChartEmpty'
import { Hicon } from '../components/Hicon'
import { DomainPageShell } from '../components/DomainPageShell'
import { KpiCard } from '../components/KpiCard'
import { PropostaIaCard, type PropostaIaState } from '../components/PropostaIaCard'
import { usePanelInsightsFoco, useRegisterPanelInsights } from '../state/PanelInsightsContext'
import { pedirAnaliseProposta } from '../api/bridge'
import { carregaMargemAnuncios, type FonteInfo } from './margemAnuncio/fonte'
import {
  agregarAnuncios,
  bannerEnvio,
  botoesPeriodo,
  brl,
  brl0,
  classeMC,
  composicaoCusto,
  contagemLogistica,
  dataBr,
  dataLabel,
  detalhe,
  diasDisponiveis,
  faixasMC,
  filtrarTabela,
  AVISO_PAYLOAD_ANTIGO,
  ROTULO_MARCA,
  ajusteRegime,
  horaBrasilia,
  kpis as calcKpis,
  ladoALado,
  montarTabs,
  naView,
  pct,
  pendencias,
  somar,
  viewTemML,
  type CmvManual,
  type Filtro,
  type FiltroTabela,
  type LinhaAnuncio,
  type RawMargem,
} from './margemAnuncio/logic'

/**
 * O painel não edita CMV (decisão da Mariana, 18/09/2026: o artifact tinha um
 * campo para digitar o custo e trazer o pedido de volta ao cálculo; aqui isso
 * saiu para não poluir). Pedido sem CMV simplesmente fica fora, e os itens são
 * listados no rodapé. As funções de `logic.ts` continuam aceitando o mapa de
 * custos manuais — a tela passa sempre vazio.
 */
const SEM_CMV_MANUAL: CmvManual = {}

const COLUNAS: Array<{ k: keyof LinhaAnuncio; label: string; num?: boolean }> = [
  { k: 't', label: 'Anúncio' },
  { k: 'acc', label: 'Loja' },
  { k: 'ped', label: 'Pedidos', num: true },
  { k: 'un', label: 'Un.', num: true },
  { k: 'rev', label: 'Receita', num: true },
  { k: 'mg', label: 'MC oficial (notas fiscais)', num: true },
  { k: 'pct', label: 'MC %', num: true },
  // ajustada = mc_ajustada da coleção margens_ajustadas (PIS/COFINS + regime de MG); marcas no tooltip
  { k: 'mgAjustada', label: 'MC ajustada (com créditos)', num: true },
  { k: 'avg', label: 'MC/pedido', num: true },
  { k: 'cmv', label: 'CMV', num: true },
  { k: 'fee', label: 'Comissão', num: true },
  { k: 'shp', label: 'Envio', num: true },
  { k: 'tax', label: 'Impostos', num: true },
]

const FILTROS: Array<{ id: FiltroTabela; label: string }> = [
  { id: '', label: 'Todos os anúncios' },
  { id: 'neg', label: 'No vermelho (MC < 0)' },
  { id: 'lo', label: 'MC abaixo de 15%' },
  { id: 'pend', label: 'Com campo zerado' },
  { id: 'limpo', label: 'Sem nenhuma pendência' },
  { id: 'cmv0', label: 'Com pedido fora (sem CMV ou CMV suspeito)' },
]

export function MargemAnunciosPage() {
  const theme = useTheme()
  const [raw, setRaw] = useState<RawMargem | null>(null)
  const [fonte, setFonte] = useState<FonteInfo | null>(null)
  const [loading, setLoading] = useState(true)
  const [atualizando, setAtualizando] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [leituraIa, setLeituraIa] = useState<PropostaIaState>({ loading: false })

  /**
   * Serviço ao vivo primeiro, arquivo estático como reserva (ver margemAnuncio/fonte.ts).
   * `fresco` = botão "Atualizar agora": ignora o cache do serviço e refaz a extração.
   */
  const load = useCallback((fresco = false) => {
    if (fresco) setAtualizando(true)
    else setLoading(true)
    setError(null)
    carregaMargemAnuncios({}, fresco)
      .then(({ raw: data, fonte: f }) => {
        setRaw(data)
        setFonte(f)
      })
      .catch((err: unknown) =>
        setError(err instanceof Error ? err.message : 'Falha ao carregar a margem por anúncio'),
      )
      .finally(() => {
        setLoading(false)
        setAtualizando(false)
      })
  }, [])

  useEffect(() => {
    load()
  }, [load])

  const horaSp = (iso: string | null | undefined) =>
    iso ? new Date(iso).toLocaleString('pt-BR', { dateStyle: 'short', timeStyle: 'short' }) : null

  const dias = useMemo(() => (raw ? diasDisponiveis(raw) : []), [raw])
  const dmin = dias[0] || ''
  const dmax = dias[dias.length - 1] || ''

  const [d1, setD1] = useState('')
  const [d2, setD2] = useState('')
  const [log, setLog] = useState('all')
  const [view, setView] = useState(-1)
  const [busca, setBusca] = useState('')
  const [filtro, setFiltro] = useState<FiltroTabela>('')
  const [sortK, setSortK] = useState<keyof LinhaAnuncio>('rev')
  const [sortD, setSortD] = useState(-1)
  const [aberto, setAberto] = useState<string | null>(null)
  const [limiteDet, setLimiteDet] = useState(60)
  const [limiteTabela, setLimiteTabela] = useState(80)
  const [notasAbertas, setNotasAbertas] = useState(false)

  useEffect(() => {
    if (dmin && dmax) {
      setD1(dmin)
      setD2(dmax)
    }
  }, [dmin, dmax])

  const filtroAtual: Filtro = useMemo(
    () => ({ d1: d1 || dmin, d2: d2 || dmax, log }),
    [d1, d2, dmin, dmax, log],
  )

  /** Base completa do período (todas as lojas). A view só recorta daqui. */
  const todos = useMemo(
    () => (raw && d1 ? agregarAnuncios(raw, filtroAtual, SEM_CMV_MANUAL) : []),
    [raw, d1, filtroAtual],
  )
  const daView = useMemo(() => todos.filter((a) => naView(a, view)), [todos, view])

  const tabs = useMemo(() => (raw ? montarTabs(raw) : []), [raw])
  const temML = useMemo(() => (raw ? viewTemML(raw, view) : false), [raw, view])
  useEffect(() => {
    if (!temML && log !== 'all') setLog('all')
  }, [temML, log])

  const k = useMemo(() => calcKpis(daView), [daView])
  /**
   * MC ajustada = soma de `mc_ajustada` da coleção `margens_ajustadas` (margem ajustada v2):
   * já traz o DIFAL fantasma devolvido, o PIS/COFINS efetivo e o regime de MG da Extrema.
   * A tela não recalcula nada; pedido sem documento entra com a oficial (marca sem_ajustada).
   */
  const mgaj = useMemo(() => somar(daView, 'mgAjustada'), [daView])
  const pctaj = k.rev ? mgaj / k.rev : 0
  const ajusteMC = mgaj - k.mg
  const banner = useMemo(
    () => (raw && d1 ? bannerEnvio(raw, filtroAtual, view, SEM_CMV_MANUAL) : null),
    [raw, d1, filtroAtual, view],
  )
  const logs = useMemo(
    () => (raw && d1 ? contagemLogistica(raw, filtroAtual, view) : null),
    [raw, d1, filtroAtual, view],
  )
  const lado = useMemo(() => (raw ? ladoALado(raw, todos) : []), [raw, todos])
  const comp = useMemo(() => composicaoCusto(daView), [daView])
  const pend = useMemo(() => pendencias(daView), [daView])
  const faixas = useMemo(() => faixasMC(daView), [daView])
  const reg = useMemo(
    () => (raw && d1 ? ajusteRegime(raw, filtroAtual, view, SEM_CMV_MANUAL, daView) : null),
    [raw, d1, filtroAtual, view, daView],
  )
  /** "dd/mm hh:mm" (Brasília) da última rodada da coleção; null = vazia ou arquivo antigo */
  const calcEm = horaBrasilia(raw?.ajustada?.calculada_em)
  const avisoAjustada = raw
    ? raw.ajustada === undefined
      ? AVISO_PAYLOAD_ANTIGO
      : raw.ajustada.aviso || null
    : null
  const rotuloAjustada = `MC ajustada (com créditos)${calcEm ? ` · calculada em ${calcEm}` : ''}`
  const semCmv = useMemo(
    () => daView.filter((a) => a.exPed > 0 || a.temMan).sort((a, b) => b.exRev - a.exRev || b.rev - a.rev),
    [daView],
  )
  const linhas = useMemo(
    () => filtrarTabela(daView, busca, filtro, sortK, sortD),
    [daView, busca, filtro, sortK, sortD],
  )
  useEffect(() => setLimiteTabela(80), [busca, filtro, view, d1, d2, log])

  const det = useMemo(
    () => (raw && aberto ? detalhe(raw, aberto, filtroAtual, SEM_CMV_MANUAL) : null),
    [raw, aberto, filtroAtual],
  )

  const corMC = (p: number) => {
    const c = classeMC(p)
    return c === 'neg'
      ? theme.palette.error.main
      : c === 'mid'
        ? theme.palette.warning.main
        : theme.palette.success.main
  }

  const ordenar = (kk: keyof LinhaAnuncio) => {
    if (sortK === kk) setSortD((d) => -d)
    else {
      setSortK(kk)
      setSortD(kk === 'ad' || kk === 't' ? 1 : -1)
    }
  }

  const periodo = raw?.periodo
  const meta = raw?.meta_bi
  const diasNoRecorte = dias.filter((d) => d >= filtroAtual.d1 && d <= filtroAtual.d2).length
  const periodoCompleto = filtroAtual.d1 === dmin && filtroAtual.d2 === dmax

  const { focoRef } = usePanelInsightsFoco()
  const carregarLeitura = useCallback(async () => {
    if (!raw || loading || error) return
    setLeituraIa((prev) => ({ ...prev, loading: true, erro: null }))
    const fallback = [
      `Receita considerada R$ ${brl0(k.rev)} · MC oficial R$ ${brl0(k.mg)} (${pct(k.pct)}) · MC ajustada R$ ${brl0(mgaj)} (${pct(pctaj)}).`,
      `Ajuste vs oficial: ${ajusteMC >= 0 ? '+' : ''}R$ ${brl0(ajusteMC)}.`,
      `${brl0(k.anuncios)} anúncios · ${brl0(k.ped)} pedidos · ${brl0(k.anunciosNoVermelho)} no vermelho.`,
      k.exPed ? `${brl0(k.exPed)} pedidos fora por falta de CMV.` : '',
      `Verdade = KPIs e tabelas desta tela (artifact ${periodo?.gerado || 'estático'}).`,
    ]
      .filter(Boolean)
      .join('\n')
    const data = await pedirAnaliseProposta({
      tipo: 'insight_reflexao',
      insight: {
        dominio: 'margem_anuncios',
        dominio_label: 'Margem por Anúncio',
        titulo: 'Leitura MC oficial × ajustada',
        leitura: fallback,
        ...(focoRef.current ? { foco_usuario: focoRef.current } : {}),
        periodo: periodo?.label || null,
        metricas: {
          receita: k.rev,
          mc_oficial: k.mg,
          mc_oficial_pct: k.pct,
          mc_ajustada: mgaj,
          mc_ajustada_pct: pctaj,
          ajuste: ajusteMC,
          anuncios: k.anuncios,
          pedidos: k.ped,
          no_vermelho: k.anunciosNoVermelho,
          fora_sem_cmv: k.exPed,
        },
      },
    })
    if (!data.ok) {
      setLeituraIa({ loading: false, texto: fallback, fonte: 'regras', modelo: null, erro: null })
      return
    }
    setLeituraIa({
      loading: false,
      texto: data.leitura || data.texto || fallback,
      modelo: data.modelo,
      fonte: data.fonte,
      cached: data.cached,
      gerado_em: data.gerado_em,
    })
  }, [raw, loading, error, k, mgaj, pctaj, ajusteMC, periodo?.label, periodo?.gerado])

  useEffect(() => {
    if (!raw || loading || error) return
    void carregarLeitura()
  }, [raw, loading, error, view, d1, d2, log, carregarLeitura])

  useRegisterPanelInsights({
    titulo: 'Margem por Anúncio',
    verdade: 'Verdade = KPIs e tabelas desta tela (MC oficial e ajustada). Proposta = leitura da IA — revise antes de agir.',
    estado: leituraIa,
    recarregar: carregarLeitura,
  })

  return (
    <DomainPageShell
      title="Margem por Anúncio"
      subtitle={
        periodo?.gerado
          ? `${dataLabel(filtroAtual.d1 || dmin)} a ${dataLabel(filtroAtual.d2 || dmax)} · ${brl0(k.anuncios)} anúncios · ${
              fonte?.origem === 'api'
                ? `consulta ao vivo em ${periodo.gerado}${fonte.ultimoCalculo ? ` · base de margem calculada pela última vez em ${horaSp(fonte.ultimoCalculo)}` : ''}`
                : `ARQUIVO ESTÁTICO extraído em ${periodo.gerado} — o serviço ao vivo não respondeu${fonte?.motivoArquivo ? ` (${fonte.motivoArquivo})` : ''}`
            }`
          : 'Margem de contribuição por anúncio — Mercado Livre, Shopee, Shein, Amazon e TikTok'
      }
      loading={loading}
      error={error}
      onRetry={() => load()}
    >
      {!loading && !raw && !error ? (
        <ChartEmpty
          title="Nenhum dado carregado."
          hint="Nem o serviço /api/margem-anuncios nem o arquivo public/data/margem-anuncios.json.gz responderam."
          suggestPeriod={false}
        />
      ) : null}

      {raw && fonte?.origem === 'arquivo' ? (
        <Alert severity="warning" sx={{ mb: 1 }}>
          Você está vendo o <b>arquivo estático de {periodo?.gerado}</b>, não a base ao vivo: o serviço
          de Margem por Anúncio não respondeu{fonte.motivoArquivo ? ` (${fonte.motivoArquivo})` : ''}.
          Os pedidos depois dessa data não aparecem aqui.
        </Alert>
      ) : null}
      {raw && fonte?.aviso ? <Alert severity="info" sx={{ mb: 1 }}>{fonte.aviso}</Alert> : null}
      {raw && avisoAjustada ? (
        <Alert severity="warning" sx={{ mb: 1 }} data-testid="aviso-ajustada">
          <b>Margem ajustada indisponível:</b> {avisoAjustada}. Enquanto isso a <b>MC ajustada</b> aparece
          igual à <b>MC oficial (notas fiscais)</b>, que segue intacta — nenhuma estimativa é feita na tela.
        </Alert>
      ) : null}

      {raw && d1 ? (
        <Stack spacing={2.5}>
          {/* ---------- período ---------- */}
          <Card>
            <CardContent>
              <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap alignItems="center">
                <Typography variant="subtitle2" color="text.secondary" sx={{ mr: 0.5 }}>
                  PERÍODO
                </Typography>
                {botoesPeriodo(dias).map((b) => (
                  <Button
                    key={b.label}
                    size="small"
                    variant={filtroAtual.d1 === b.d1 && filtroAtual.d2 === b.d2 ? 'contained' : 'outlined'}
                    onClick={() => {
                      setD1(b.d1)
                      setD2(b.d2)
                    }}
                  >
                    {b.label}
                  </Button>
                ))}
                <Box sx={{ flex: 1 }} />
                <TextField
                  size="small"
                  type="date"
                  label="De"
                  value={d1}
                  slotProps={{ inputLabel: { shrink: true }, htmlInput: { min: dmin, max: dmax } }}
                  onChange={(e) => {
                    const v = e.target.value || dmin
                    setD1(v)
                    if (v > d2) setD2(v)
                  }}
                />
                <TextField
                  size="small"
                  type="date"
                  label="Até"
                  value={d2}
                  slotProps={{ inputLabel: { shrink: true }, htmlInput: { min: dmin, max: dmax } }}
                  onChange={(e) => {
                    const v = e.target.value || dmax
                    setD2(v)
                    if (v < d1) setD1(v)
                  }}
                />
              </Stack>
              <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap sx={{ mt: 1 }}>
                <Typography variant="caption" color="text.secondary">
                  {periodoCompleto ? 'Período completo · ' : ''}
                  {diasNoRecorte} {diasNoRecorte === 1 ? 'dia' : 'dias'} com pedido
                  {fonte?.origem === 'api'
                    ? ` · ao vivo de mongodb_financial.margens (consulta ${periodo?.gerado}${fonte.ultimoCalculo ? `, último cálculo da base ${horaSp(fonte.ultimoCalculo)}` : ''})`
                    : meta?.exportado_em
                      ? ` · arquivo exportado em ${meta.exportado_em.slice(0, 16).replace('T', ' ')}`
                      : ''}
                </Typography>
                <Box sx={{ flex: 1 }} />
                <Button
                  size="small"
                  variant="outlined"
                  disabled={atualizando || loading}
                  onClick={() => load(true)}
                  title="Refaz a consulta à base de margem agora, ignorando o cache do serviço (alguns segundos)."
                >
                  {atualizando ? 'Consultando a base…' : 'Atualizar agora'}
                </Button>
              </Stack>

              <Divider sx={{ my: 1.5 }} />

              <Stack direction="row" spacing={0.75} flexWrap="wrap" useFlexGap>
                {tabs.map(([label, v]) => (
                  <Chip
                    key={`${label}-${v}`}
                    label={label}
                    size="small"
                    color={view === v ? 'primary' : 'default'}
                    variant={view === v ? 'filled' : 'outlined'}
                    onClick={() => setView(v)}
                  />
                ))}
              </Stack>

              {temML && logs ? (
                <>
                  <Stack direction="row" spacing={0.75} flexWrap="wrap" useFlexGap sx={{ mt: 1.5 }}>
                    <Typography variant="caption" color="text.secondary" sx={{ alignSelf: 'center', mr: 0.5 }}>
                      ENVIO (ML)
                    </Typography>
                    {logs.linhas.map((l) => (
                      <Tooltip key={l.chave} title={`${brl0(l.pedidos)} pedidos · R$ ${brl0(l.receita)}`}>
                        <Chip
                          label={`${l.rotulo} ${brl0(l.pedidos)}`}
                          size="small"
                          variant={log === l.chave ? 'filled' : 'outlined'}
                          color={log === l.chave ? 'secondary' : 'default'}
                          onClick={() => setLog(l.chave)}
                        />
                      </Tooltip>
                    ))}
                  </Stack>
                  {logs.pctNaoIdentificado > 0 ? (
                    <Typography variant="caption" color="text.secondary" sx={{ mt: 0.75, display: 'block' }}>
                      <b>{pct(logs.pctNaoIdentificado)}</b> dos pedidos do ML sem tipo de envio gravado — o campo
                      ficou vazio em julho e em boa parte de agosto. Esse grupo nunca é escondido nem chutado.
                    </Typography>
                  ) : null}
                </>
              ) : null}
            </CardContent>
          </Card>

          {/* ---------- KPIs ---------- */}
          <Box
            sx={{
              display: 'grid',
              gap: 1.5,
              gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)', md: 'repeat(3, 1fr)', lg: 'repeat(5, 1fr)' },
            }}
          >
            <KpiCard
              label="Receita considerada"
              value={`R$ ${brl0(k.rev)}`}
              hint={`order_total · ${brl0(k.un)} unidades · ${brl0(k.ped)} pedidos${k.exPed ? ` · ${brl0(k.exPed)} fora` : ''}`}
            />
            <KpiCard
              label="MC oficial (notas fiscais)"
              value={`R$ ${brl0(k.mg)}`}
              hint={`${pct(k.pct)} da receita · imposto como saiu na NF (campo taxes)`}
              badge={k.mg < 0 ? 'no vermelho' : null}
            />
            <KpiCard
              label={rotuloAjustada}
              value={`R$ ${brl0(mgaj)}`}
              hint={
                avisoAjustada
                  ? `${pct(pctaj)} da receita · igual à oficial — coleção indisponível`
                  : `${pct(pctaj)} da receita · ${ajusteMC >= 0 ? '+' : ''}R$ ${brl0(ajusteMC)} vs. oficial — PIS/COFINS efetivo + regime de MG (Extrema)${reg?.pedidosComMarca ? ` · ${brl0(reg.pedidosComMarca)} pedidos com marca` : ''}`
              }
            />
            <KpiCard label="MC média por pedido" value={`R$ ${brl(k.mcPorPedido)}`} hint={`ticket médio R$ ${brl(k.ticket)}`} />
            <KpiCard
              label="Receita da loja"
              value={`R$ ${brl0(k.receitaDaLoja)}`}
              hint={
                k.gapPago > 1
                  ? `order_total − seller_discount. O paid_value gravado (R$ ${brl0(k.paid)}) é o escrow, já líquido do service_fee — não serve de receita`
                  : 'order_total − seller_discount'
              }
            />
            <KpiCard
              label="Custo do marketplace"
              value={`R$ ${brl0(k.custoMarketplace)}`}
              hint={`comissão ${brl0(k.fee)} · envio ${brl0(k.shp)}`}
            />
            <KpiCard
              label="Anúncios no vermelho"
              value={brl0(k.anunciosNoVermelho)}
              hint={`R$ ${brl0(k.receitaNoVermelho)} de receita (${pct(k.rev ? k.receitaNoVermelho / k.rev : 0)})`}
            />
            <KpiCard
              label="Anúncios sem campo zerado"
              value={brl0(k.anunciosLimpos)}
              hint={`de ${brl0(k.anuncios)} — só nestes a MC está completa`}
            />
            <KpiCard
              label="Fora do cálculo — sem CMV ou CMV suspeito"
              value={brl0(k.exPed)}
              hint={
                k.exPed
                  ? `pedidos em ${k.anunciosSemCmv} anúncios · R$ ${brl0(k.exRev)} de receita não contabilizada`
                  : 'nenhum pedido excluído'
              }
              // caveat = número incompleto: esta receita está fora da MC de propósito
              valueTone={k.exPed ? 'caveat' : 'default'}
            />
          </Box>

          {/* ---------- parcelas da margem ajustada (coleção margens_ajustadas) ---------- */}
          {reg ? (
            <Card data-testid="bloco-regime">
              <CardContent>
                <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
                  Da MC oficial à MC ajustada — regime de e-commerce de MG
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
                  Parcelas prontas da coleção <code>margens_ajustadas</code>, pedido a pedido, somadas no recorte
                  {reg.conta ? <> · regime aplicado em <b>{reg.conta}</b></> : null}
                  {reg.calculadaEm ? <> · calculada em {horaBrasilia(reg.calculadaEm)} (Brasília)</> : null}. A
                  tela não calcula nada aqui.
                </Typography>
                <Box
                  sx={{
                    display: 'grid',
                    gap: 1.25,
                    gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)', md: 'repeat(4, 1fr)' },
                    mb: 1.5,
                  }}
                >
                  {(
                    [
                      ['MC oficial (notas fiscais)', reg.mg, false],
                      ['DIFAL fantasma devolvido', reg.difalFantasma, false],
                      ['Ajuste de PIS/COFINS', reg.ajPis, false],
                      ['ICMS economizado', reg.difIcms, false],
                      ['Crédito perdido na transferência', -reg.difCmv, true],
                      ['Diferença: margens mudou depois do cálculo', reg.residuo, false],
                      [rotuloAjustada, reg.mgaj, false],
                    ] as const
                  )
                    // a linha do resíduo só aparece quando existe (≥ meio centavo); no caso normal é zero
                    .filter(([l, v]) => l !== 'Diferença: margens mudou depois do cálculo' || Math.abs(v) >= 0.005)
                    .map(([l, v, custo]) => (
                      <Box key={l}>
                        <Typography variant="caption" color="text.secondary">
                          {l}
                        </Typography>
                        <Typography
                          sx={{
                            fontVariantNumeric: 'tabular-nums',
                            fontWeight: 600,
                            color: custo && v ? theme.palette.error.main : undefined,
                          }}
                        >
                          {v < 0 ? '−' : ''}R$ {brl0(Math.abs(v))}
                          <Typography component="span" variant="caption" color="text.secondary">
                            {' '}
                            · {pct(reg.rev && v ? v / reg.rev : 0)}
                          </Typography>
                        </Typography>
                      </Box>
                    ))}
                </Box>
                <Stack direction="row" spacing={0.75} flexWrap="wrap" useFlexGap alignItems="center">
                  <Typography variant="caption" color="text.secondary" sx={{ mr: 0.5 }}>
                    MARCAS ({brl0(reg.pedidosComMarca)} de {brl0(reg.pedidos)} pedidos)
                  </Typography>
                  {Object.entries(reg.marcas)
                    .sort((x, y) => y[1] - x[1])
                    .map(([m, n]) => (
                      <Tooltip key={m} title={ROTULO_MARCA[m] || m}>
                        <Chip label={`${m} · ${brl0(n)}`} size="small" variant="outlined" data-testid="marca" />
                      </Tooltip>
                    ))}
                  {!Object.keys(reg.marcas).length ? (
                    <Typography variant="caption" color="text.secondary">
                      nenhuma
                    </Typography>
                  ) : null}
                </Stack>
                {reg.versao ? (
                  <Typography variant="caption" color="text.disabled" sx={{ display: 'block', mt: 1 }}>
                    versão da regra {reg.versao}
                  </Typography>
                ) : null}
              </CardContent>
            </Card>
          ) : null}

          <PropostaIaCard
            title="Leitura da Margem por Anúncio"
            caption="Verdade = KPIs e tabelas (MC oficial e ajustada). Proposta = leitura da IA — revise antes de agir. Snapshot estático do pipeline, não o GET live /margens."
            state={leituraIa}
            onRetry={() => void carregarLeitura()}
          />

          {/* ---------- aviso curto; o detalhe fica no rodapé ---------- */}
          {banner?.mostrarML && banner.semEnv ? (
            <Alert severity="warning" sx={{ py: 0.5 }}>
              <b>{brl0(banner.semEnv)}</b> de {brl0(banner.pedML)} pedidos do Mercado Livre estão sem envio lançado —
              a MC deles aparece maior do que é. Detalhe no rodapé.
            </Alert>
          ) : null}

          {/* ---------- canais e lojas ---------- */}
          <Card>
            <CardContent>
              <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 1.5 }}>
                Canais e lojas, lado a lado
              </Typography>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Loja</TableCell>
                    <TableCell align="right">Receita</TableCell>
                    <TableCell align="right">MC</TableCell>
                    <TableCell align="right">MC %</TableCell>
                    <TableCell align="right">Pedidos</TableCell>
                    <TableCell align="right">MC/pedido</TableCell>
                    <TableCell sx={{ width: '22%' }}>Participação na receita</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {lado.map((l) => (
                    <TableRow
                      key={`${l.label}-${l.view}`}
                      hover
                      onClick={() => setView(l.view)}
                      sx={{
                        cursor: 'pointer',
                        fontWeight: l.grupo ? 600 : 400,
                        bgcolor: l.view === view ? alpha(theme.palette.primary.main, 0.08) : undefined,
                      }}
                    >
                      <TableCell sx={{ fontWeight: l.grupo ? 700 : 400, pl: l.grupo ? 2 : 4 }}>
                        {l.label}
                        <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                          {l.sub}
                        </Typography>
                      </TableCell>
                      <TableCell align="right" sx={{ fontVariantNumeric: 'tabular-nums' }}>
                        R$ {brl0(l.rev)}
                      </TableCell>
                      <TableCell align="right" sx={{ color: corMC(l.pct), fontVariantNumeric: 'tabular-nums' }}>
                        R$ {brl0(l.mg)}
                      </TableCell>
                      <TableCell align="right" sx={{ color: corMC(l.pct) }}>
                        {pct(l.pct)}
                      </TableCell>
                      <TableCell align="right">{brl0(l.ped)}</TableCell>
                      <TableCell align="right">R$ {brl(l.ped ? l.mg / l.ped : 0)}</TableCell>
                      <TableCell>
                        <LinearProgress
                          variant="determinate"
                          value={Math.min(l.participacao * 100, 100)}
                          sx={{
                            height: 9,
                            borderRadius: 1,
                            bgcolor: alpha(theme.palette.text.primary, 0.08),
                            '& .MuiLinearProgress-bar': { bgcolor: corMC(l.pct) },
                          }}
                        />
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </CardContent>
          </Card>

          {/* ---------- faixas ---------- */}
          <Card>
            <CardContent>
              <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 1.5 }}>
                Distribuição dos anúncios por faixa de margem de contribuição
              </Typography>
              <Stack spacing={1}>
                {faixas.map((f) => {
                  const maior = Math.max(...faixas.map((x) => x.rev), 1)
                  return (
                    <Stack key={f.label} direction="row" spacing={1.5} alignItems="center">
                      <Typography variant="body2" sx={{ width: 74, flexShrink: 0 }}>
                        {f.label}
                      </Typography>
                      <Box sx={{ flex: 1 }}>
                        <LinearProgress
                          variant="determinate"
                          value={(f.rev / maior) * 100}
                          sx={{
                            height: 12,
                            borderRadius: 1,
                            bgcolor: alpha(theme.palette.text.primary, 0.08),
                            '& .MuiLinearProgress-bar': {
                              bgcolor: f.cor === 'bad' ? theme.palette.error.main : corMC(f.cor === 'b1' ? 0.05 : 0.3),
                            },
                          }}
                        />
                      </Box>
                      <Typography variant="caption" color="text.secondary" sx={{ width: 240, textAlign: 'right' }}>
                        {f.n} anúncios · R$ {brl0(f.rev)} ({pct(f.pctRev)})
                      </Typography>
                    </Stack>
                  )
                })}
              </Stack>
            </CardContent>
          </Card>

          {/* ---------- tabela ---------- */}
          <Card>
            <CardContent>
              <Stack direction="row" spacing={1.5} alignItems="center" flexWrap="wrap" useFlexGap sx={{ mb: 1.5 }}>
                <Typography variant="subtitle1" sx={{ fontWeight: 700, flexShrink: 0 }}>
                  Anúncios
                </Typography>
                <TextField
                  size="small"
                  placeholder="Buscar por MLB, título ou SKU"
                  value={busca}
                  onChange={(e) => setBusca(e.target.value)}
                  sx={{ minWidth: 260, flex: 1 }}
                />
                <TextField
                  size="small"
                  select
                  value={filtro}
                  onChange={(e) => setFiltro(e.target.value as FiltroTabela)}
                  sx={{ minWidth: 240 }}
                >
                  {FILTROS.map((f) => (
                    <MenuItem key={f.id} value={f.id}>
                      {f.label}
                    </MenuItem>
                  ))}
                </TextField>
                <Typography variant="caption" color="text.secondary">
                  {linhas.length} anúncios · R$ {brl0(somar(linhas, 'rev'))} de receita
                </Typography>
              </Stack>

              {linhas.length === 0 ? (
                <ChartEmpty
                  title="Nenhum anúncio com esses filtros."
                  hint="Limpe a busca ou escolha outro recorte."
                  suggestPeriod={false}
                  dense
                />
              ) : (
                <Box sx={{ overflowX: 'auto' }}>
                  <Table size="small" sx={{ minWidth: 1100 }}>
                    <TableHead>
                      <TableRow>
                        <TableCell sx={{ width: 28 }} />
                        {COLUNAS.map((c) => (
                          <TableCell
                            key={c.k}
                            align={c.num ? 'right' : 'left'}
                            onClick={() => ordenar(c.k)}
                            sx={{ cursor: 'pointer', whiteSpace: 'nowrap', userSelect: 'none' }}
                          >
                            {c.label}
                            {sortK === c.k ? (sortD > 0 ? ' ▲' : ' ▼') : ''}
                          </TableCell>
                        ))}
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {linhas.slice(0, limiteTabela).map((a) => (
                        <Fragment key={a.ad}>
                          <TableRow
                            hover
                            onClick={() => setAberto((x) => (x === a.ad ? null : a.ad))}
                            sx={{ cursor: 'pointer' }}
                          >
                            <TableCell>{aberto === a.ad ? '▼' : '▶'}</TableCell>
                            <TableCell sx={{ maxWidth: 380 }}>
                              <Typography variant="body2" noWrap title={a.t}>
                                {a.t || '—'}
                              </Typography>
                              <Typography variant="caption" color="text.secondary">
                                {a.ad}
                                {a.sk ? ` · ${a.sk}` : ''}
                                {a.exPed ? ` · ${a.exPed} fora (${a.n7 ? 'CMV suspeito' : 'sem CMV'})` : ''}
                                {a.temMan ? ' · CMV informado' : ''}
                              </Typography>
                            </TableCell>
                            <TableCell>
                              <Chip label={raw.short[a.acc] || '?'} size="small" variant="outlined" />
                            </TableCell>
                            <TableCell align="right">{brl0(a.ped)}</TableCell>
                            <TableCell align="right">{brl0(a.un)}</TableCell>
                            <TableCell align="right" sx={{ fontVariantNumeric: 'tabular-nums' }}>
                              R$ {brl0(a.rev)}
                            </TableCell>
                            <TableCell
                              align="right"
                              sx={{ color: corMC(a.pct), fontWeight: 600, fontVariantNumeric: 'tabular-nums' }}
                            >
                              {brl0(a.mg)}
                            </TableCell>
                            <TableCell align="right" sx={{ color: corMC(a.pct) }}>
                              {pct(a.pct)}
                            </TableCell>
                            <TableCell
                              align="right"
                              title={
                                `oficial ${pct(a.pct)} · ajustada ${pct(a.pctAjustada)}` +
                                ` · PIS/COFINS ${a.ajPis >= 0 ? '+' : ''}${brl(a.ajPis)}` +
                                (a.difalFantasma ? ` · DIFAL fantasma +${brl(a.difalFantasma)}` : '') +
                                (a.difIcms || a.difCmv
                                  ? ` · ICMS economizado +${brl(a.difIcms)} · crédito perdido −${brl(a.difCmv)}`
                                  : '') +
                                (Math.abs(a.residuo) >= 0.005
                                  ? ` · margens mudou depois do cálculo ${a.residuo >= 0 ? '+' : '−'}${brl(Math.abs(a.residuo))}`
                                  : '') +
                                (a.nMarca
                                  ? ` · marcas: ${Object.entries(a.marcas).map(([m, n]) => `${m} ${n}`).join(', ')}`
                                  : '')
                              }
                              sx={{
                                color: corMC(a.pctAjustada),
                                fontWeight: 600,
                                fontVariantNumeric: 'tabular-nums',
                              }}
                            >
                              {brl0(a.mgAjustada)} · {pct(a.pctAjustada)}
                            </TableCell>
                            <TableCell align="right">R$ {brl(a.avg)}</TableCell>
                            <TableCell align="right">{brl0(a.cmv)}</TableCell>
                            <TableCell align="right">{brl0(a.fee)}</TableCell>
                            <TableCell align="right">{a.shp ? brl0(a.shp) : '—'}</TableCell>
                            <TableCell align="right">{brl0(a.tax)}</TableCell>
                          </TableRow>
                          {aberto === a.ad && det ? (
                            <TableRow>
                              <TableCell colSpan={COLUNAS.length + 1} sx={{ bgcolor: alpha(theme.palette.text.primary, 0.03) }}>
                                <Typography variant="subtitle2" sx={{ mb: 1 }}>
                                  Detalhe pedido a pedido — {det.linhas.length} pedido
                                  {det.linhas.length !== 1 ? 's' : ''} (mais recentes primeiro)
                                  {det.fora ? ` · ${det.fora} fora do total` : ''}
                                </Typography>
                                <Box sx={{ overflowX: 'auto' }}>
                                  <Table size="small" sx={{ minWidth: 1180 }}>
                                    <TableHead>
                                      <TableRow>
                                        <TableCell>Pedido</TableCell>
                                        <TableCell>Data</TableCell>
                                        <TableCell align="right">Qtd</TableCell>
                                        <TableCell align="right">Receita</TableCell>
                                        <TableCell align="right">Pago</TableCell>
                                        <TableCell align="right">CMV</TableCell>
                                        <TableCell align="right">Comissão</TableCell>
                                        <TableCell align="right">Envio</TableCell>
                                        <TableCell align="right">Impostos</TableCell>
                                        <TableCell align="right">Desconto</TableCell>
                                        <TableCell align="right">MC oficial</TableCell>
                                        <TableCell align="right">MC %</TableCell>
                                        <TableCell
                                          align="right"
                                          title="MC ajustada (com créditos) — pronta da coleção margens_ajustadas, pedido a pedido"
                                        >
                                          MC ajustada
                                        </TableCell>
                                        <TableCell align="right">MC aj. %</TableCell>
                                        <TableCell>Alerta</TableCell>
                                      </TableRow>
                                    </TableHead>
                                    <TableBody>
                                      {det.linhas.slice(0, limiteDet).map((l) => (
                                        <TableRow key={l.pedido} sx={{ opacity: l.fora ? 0.55 : 1 }}>
                                          <TableCell>{l.pedido}</TableCell>
                                          <TableCell>{dataBr(l.data)}</TableCell>
                                          <TableCell align="right">{l.qtd}</TableCell>
                                          <TableCell align="right">R$ {brl(l.receita)}</TableCell>
                                          <TableCell align="right">{brl(l.pago)}</TableCell>
                                          <TableCell
                                            align="right"
                                            sx={{ color: l.cmvInformado ? 'secondary.main' : undefined, fontWeight: l.cmvInformado ? 600 : 400 }}
                                          >
                                            {/* CMV suspeito continua visível: o erro é o achado */}
                                            {l.fora && !l.cmv ? '—' : brl(l.cmv)}
                                          </TableCell>
                                          <TableCell align="right">{brl(l.fee)}</TableCell>
                                          <TableCell
                                            align="right"
                                            title={l.corr > 0 ? `sinal corrigido no painel: a base gravou −${brl(l.corr)}` : undefined}
                                            sx={{ color: l.corr > 0 ? 'secondary.main' : l.shp < 0 ? 'error.main' : undefined }}
                                          >
                                            {l.shp ? brl(l.shp) : '—'}
                                          </TableCell>
                                          <TableCell align="right">{brl(l.tax)}</TableCell>
                                          <TableCell align="right">{l.dsc ? brl(l.dsc) : '—'}</TableCell>
                                          <TableCell align="right" sx={{ color: l.fora ? 'text.disabled' : corMC(l.pct), fontWeight: 600 }}>
                                            {l.fora ? '—' : brl(l.mc)}
                                          </TableCell>
                                          <TableCell align="right" sx={{ color: l.fora ? 'text.disabled' : corMC(l.pct) }}>
                                            {l.fora ? '—' : pct(l.pct)}
                                          </TableCell>
                                          {/* MC ajustada linha a linha (ela, 15/10/2026); parcelas no tooltip, igual à coluna do agregado */}
                                          <TableCell
                                            align="right"
                                            title={
                                              l.fora
                                                ? undefined
                                                : `oficial ${pct(l.pct)} · ajustada ${pct(l.pctaj)}` +
                                                  ` · PIS/COFINS ${l.ajPis >= 0 ? '+' : ''}${brl(l.ajPis)}` +
                                                  (l.difalFantasma ? ` · DIFAL fantasma +${brl(l.difalFantasma)}` : '') +
                                                  (l.difIcms || l.difCmv
                                                    ? ` · ICMS economizado +${brl(l.difIcms)} · crédito perdido −${brl(l.difCmv)}`
                                                    : '') +
                                                  (Math.abs(l.residuo) >= 0.005
                                                    ? ` · margens mudou depois do cálculo ${l.residuo >= 0 ? '+' : '−'}${brl(Math.abs(l.residuo))}`
                                                    : '') +
                                                  (l.marcas.length ? ` · marcas: ${l.marcas.join(', ')}` : '')
                                            }
                                            sx={{ color: l.fora ? 'text.disabled' : corMC(l.pctaj), fontWeight: 600 }}
                                          >
                                            {l.fora ? '—' : brl(l.mcaj)}
                                          </TableCell>
                                          <TableCell align="right" sx={{ color: l.fora ? 'text.disabled' : corMC(l.pctaj) }}>
                                            {l.fora ? '—' : pct(l.pctaj)}
                                          </TableCell>
                                          <TableCell>
                                            <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap>
                                              {l.tags.map((t) => (
                                                <Chip key={t} label={t} size="small" variant="outlined" />
                                              ))}
                                            </Stack>
                                          </TableCell>
                                        </TableRow>
                                      ))}
                                    </TableBody>
                                  </Table>
                                </Box>
                                {det.linhas.length > limiteDet ? (
                                  <Button size="small" sx={{ mt: 1 }} onClick={() => setLimiteDet((n) => n + 200)}>
                                    Mostrar os {det.linhas.length - limiteDet} pedidos restantes
                                  </Button>
                                ) : null}
                                <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                                  Total no período: R$ {brl(det.totais.receita)} de receita · CMV {brl(det.totais.cmv)} ·
                                  comissão {brl(det.totais.fee)} · envio {brl(det.totais.shp)} · impostos{' '}
                                  {brl(det.totais.tax)} · <b>MC oficial {brl(det.totais.mc)} ({pct(det.totais.pct)})</b> ·{' '}
                                  <b>MC ajustada {brl(det.totais.mcaj)} ({pct(det.totais.pctaj)})</b>
                                </Typography>
                              </TableCell>
                            </TableRow>
                          ) : null}
                        </Fragment>
                      ))}
                    </TableBody>
                  </Table>
                </Box>
              )}
              {linhas.length > limiteTabela ? (
                <Button size="small" sx={{ mt: 1.5 }} onClick={() => setLimiteTabela((n) => n + 120)}>
                  Carregar mais {Math.min(120, linhas.length - limiteTabela)} anúncios
                </Button>
              ) : null}
            </CardContent>
          </Card>

          {/* ---------- rodapé: toda a metodologia, recolhido por padrão ---------- */}
          <Accordion
            expanded={notasAbertas}
            onChange={() => setNotasAbertas((v) => !v)}
            disableGutters
            sx={{ '&::before': { display: 'none' }, borderRadius: 1, border: 1, borderColor: 'divider' }}
          >
            <AccordionSummary expandIcon={<Hicon name="chevron-down" sx={{ fontSize: 20 }} />}>
              <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                Como estes números são calculados
              </Typography>
              <Typography variant="caption" color="text.secondary" sx={{ ml: 1.5, alignSelf: 'center' }}>
                origem, fórmula, o que fica de fora e a conferência da base
              </Typography>
            </AccordionSummary>
            <AccordionDetails>
              <Stack spacing={2.5} divider={<Divider />}>
                {/* origem e fórmula */}
                <Box>
                  <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 0.75 }}>
                    Origem e fórmula
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Todos os valores vêm de <code>mongodb_financial.margens</code> com <code>type:"pedido"</code>,{' '}
                    <b>sem estimativa</b> e sem nada da API do Mercado Livre. A margem é o campo{' '}
                    <code>calculated_margin</code>, como a base gravou:
                  </Typography>
                  <Typography
                    variant="body2"
                    sx={{
                      my: 1,
                      p: 1.25,
                      bgcolor: alpha(theme.palette.text.primary, 0.04),
                      borderRadius: 1,
                      fontFamily: 'monospace',
                    }}
                  >
                    MC = receita − CMV − comissão − envio − impostos − descontos + reembolso
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    A fórmula é conferida <b>pedido a pedido</b> pelo pipeline antes de o arquivo ser publicado — se
                    algum registro não fechar, ele aparece marcado como &ldquo;fórmula não fecha&rdquo;. A loja de cada
                    pedido vem de <code>mongodb_financial.pedidos</code> (<code>store_name</code> /{' '}
                    <code>store_key</code>). Período por <code>reference_date</code> entre {periodo?.ini} e{' '}
                    {periodo?.fim} (inclusive), UTC.
                    {periodo?.gerado ? <> Extração gerada em {periodo.gerado}.</> : null}
                  </Typography>
                </Box>

                {/* o que fica fora */}
                <Box>
                  <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 0.75 }}>
                    O que não entra no cálculo
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                    <b>Pedido sem CMV cadastrado, ou com CMV maior que a própria receita, não entra no cálculo da margem</b>
                    {' '}— sem custo a MC pareceria maior do que é; com custo inflado (carga da <code>cmvs</code> que gravou
                    3,477 como 3477, em 06/10/2026) um único pedido de R$ 42 derrubava a MC em R$ 11 mil. No recorte atual
                    são <b>{brl0(k.exPed)} pedidos</b> em {k.anunciosSemCmv} anúncios, somando R$ {brl0(k.exRev)} de
                    receita que fica de fora. Para corrigir de verdade, o custo precisa ser cadastrado (ou recarregado)
                    em <code>cmvs</code> e a margem recalculada.
                  </Typography>
                  {semCmv.length ? (
                    <Box sx={{ maxHeight: 260, overflow: 'auto', border: 1, borderColor: 'divider', borderRadius: 1 }}>
                      <Table size="small" stickyHeader>
                        <TableHead>
                          <TableRow>
                            <TableCell>Anúncio com pedido fora</TableCell>
                            <TableCell align="right">Pedidos fora</TableCell>
                            <TableCell align="right">Receita fora</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {semCmv.map((a) => (
                            <TableRow key={a.ad}>
                              <TableCell>
                                <Typography variant="body2" noWrap sx={{ maxWidth: 460 }} title={a.t}>
                                  {a.t || '—'}
                                </Typography>
                                <Typography variant="caption" color="text.secondary">
                                  {a.ad}
                                  {a.sk ? ` · ${a.sk}` : ''}
                                </Typography>
                              </TableCell>
                              <TableCell align="right">{brl0(a.exPed)}</TableCell>
                              <TableCell align="right">R$ {brl0(a.exRev)}</TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </Box>
                  ) : (
                    <Typography variant="body2" color="text.secondary">
                      Nenhum anúncio sem CMV neste recorte.
                    </Typography>
                  )}
                </Box>

                {/* campo a campo */}
                <Box>
                  <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 0.75 }}>
                    O que é cada campo
                  </Typography>
                  <Stack spacing={0.75}>
                    {comp.map((l) => (
                      <Typography key={l.nome} variant="body2" color="text.secondary">
                        <b>{l.nome}</b> — {l.descricao}
                      </Typography>
                    ))}
                  </Stack>
                </Box>

                {/* pedidos com campo zerado */}
                <Box>
                  <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 0.75 }}>
                    Pedidos com campo de custo zerado
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                    Campo zerado não é erro de cálculo: é custo que a base não tem. Onde falta, a MC aparece maior do
                    que é. A base do percentual muda por linha, porque nem todo campo se aplica a todo canal.
                  </Typography>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>Campo zerado</TableCell>
                        <TableCell align="right">Pedidos</TableCell>
                        <TableCell align="right">% da base aplicável</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {pend.linhas.map((l) => (
                        <TableRow key={l.descricao}>
                          <TableCell>
                            <Typography variant="body2">{l.descricao}</Typography>
                          </TableCell>
                          <TableCell align="right" sx={{ fontVariantNumeric: 'tabular-nums' }}>
                            {brl0(l.pedidos)}
                          </TableCell>
                          <TableCell align="right">
                            {pct(l.pctBase)}{' '}
                            <Typography component="span" variant="caption" color="text.secondary">
                              {l.legenda}
                            </Typography>
                          </TableCell>
                        </TableRow>
                      ))}
                      <TableRow sx={{ '& td': { borderTop: 2, fontWeight: 700 } }}>
                        <TableCell>Total de ocorrências em {brl0(pend.pedidos)} pedidos</TableCell>
                        <TableCell align="right">{brl0(pend.total)}</TableCell>
                        <TableCell align="right">—</TableCell>
                      </TableRow>
                    </TableBody>
                  </Table>
                </Box>

                {/* envio do ML */}
                {banner?.mostrarML ? (
                  <Box>
                    <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 0.75 }}>
                      Envio não lançado no Mercado Livre
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      <b>{brl0(banner.semEnv)}</b> de {brl0(banner.pedML)} pedidos do ML estão com{' '}
                      <code>shipping_cost</code> zerado (R$ {brl0(banner.revEnv)} de receita).
                      {banner.revOk ? (
                        <>
                          {' '}
                          Nos <b>{brl0(banner.pedOk)} pedidos</b> com comissão e envio gravados (R${' '}
                          {brl0(banner.revOk)} de receita) a MC é de <b>{pct(banner.mcCompletos)}</b> — contra{' '}
                          {pct(banner.mcML)} no Mercado Livre todo.
                        </>
                      ) : null}
                      {banner.negEnv ? (
                        <>
                          {' '}
                          <b>{brl0(banner.negEnv)}</b> pedidos vieram com envio negativo — dado suspeito, ficam fora
                          da MC dos pedidos completos
                          {banner.negCorr
                            ? ` (${brl0(banner.negCorr)} com o sinal confirmado pela fórmula, R$ ${brl0(banner.corrEnv)} devolvidos ao custo)`
                            : ''}
                          .
                        </>
                      ) : null}
                    </Typography>
                  </Box>
                ) : null}

                {/* Shopee */}
                {banner?.mostrarShopee ? (
                  <Box>
                    <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 0.75 }}>
                      Shopee — os campos de valor, decifrados
                    </Typography>
                    <Box
                      sx={{
                        display: 'grid',
                        gap: 1,
                        gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)', md: 'repeat(3, 1fr)' },
                        mb: 1,
                      }}
                    >
                      {[
                        ['order_total (preço cheio)', `R$ ${brl0(banner.revSH)}`],
                        ['paid_value gravado (escrow)', `R$ ${brl0(banner.paidSH)}`],
                        ['diferença bruto × pago', `R$ ${brl0(banner.gapSH)}`],
                        ['seller_discount', `R$ ${brl0(banner.dscSH)}`],
                        ['escrow_fee (comissão, já com frete)', `R$ ${brl0(banner.feeSH)}`],
                        ['receita da loja (cheio − desconto)', `R$ ${brl0(banner.receitaLojaSH)}`],
                      ].map(([l, v]) => (
                        <Box key={l}>
                          <Typography variant="caption" color="text.secondary">
                            {l}
                          </Typography>
                          <Typography sx={{ fontVariantNumeric: 'tabular-nums', fontWeight: 600 }}>{v}</Typography>
                        </Box>
                      ))}
                    </Box>
                    {banner.rebSH ? (
                      <Typography variant="body2" color="text.secondary">
                        Reembolso (<code>rebate</code>) no período: R$ {brl0(banner.rebSH)}. A base tem{' '}
                        <b>dois modelos</b> convivendo desde 28/08/2026 — em uns o reembolso soma na margem, em outros
                        já vem embutido na receita. O pipeline testa os dois <b>por pedido</b> e só marca erro quando
                        nenhum fecha.
                      </Typography>
                    ) : null}
                  </Box>
                ) : null}

                {/* tipo de envio */}
                {logs && logs.total ? (
                  <Box>
                    <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 0.75 }}>
                      Tipo de envio (Full x normal)
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Vem de <code>shippings.logistic_type</code>, ligado ao pedido por <code>orders</code>. O campo
                      parou de ser gravado em julho de 2026 e voltou em setembro — por isso existe o grupo &ldquo;não
                      identificado&rdquo;, que <b>nunca é escondido nem chutado</b>. Neste recorte,{' '}
                      <b>{pct(logs.pctNaoIdentificado)}</b> dos {brl0(logs.total)} pedidos do ML estão sem tipo de
                      envio gravado.
                    </Typography>
                  </Box>
                ) : null}

                {/* as duas margens */}
                <Box>
                  <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 0.75 }}>
                    As duas margens
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                    <b>MC oficial</b> é a das <b>notas fiscais</b> — o imposto entra como saiu na NF (campo{' '}
                    <code>taxes</code>, somando ICMS, DIFAL e PIS/COFINS). É a margem principal, e é a que vale para
                    comparação entre canais. <b>MC ajustada</b> é a mesma margem <b>com os créditos</b> considerados.
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                    A ajustada vem <b>pronta</b> da coleção <code>margens_ajustadas</code> (serviço{' '}
                    <code>services/margem-ajustada</code>, regra na skill <code>margem-ajustada-fiesta</code>) — a
                    tela só soma. Ela tem <b>três parcelas</b> sobre a oficial: o <b>DIFAL fantasma devolvido</b>{' '}
                    (DIFAL de 6% lançado em venda dentro de MG — skill §5), o <b>PIS/COFINS efetivo</b> (matriz e
                    Extrema) e o <b>regime de e-commerce de MG</b> (só Extrema: ICMS economizado menos o crédito
                    perdido na transferência). A de PIS/COFINS segue a regra:
                  </Typography>
                  <Typography
                    variant="body2"
                    sx={{
                      my: 1,
                      p: 1.25,
                      bgcolor: alpha(theme.palette.text.primary, 0.04),
                      borderRadius: 1,
                      fontFamily: 'monospace',
                      whiteSpace: 'pre-line',
                    }}
                  >
                    {'PIS/COFINS da venda    = f × 9,25% × (valor da venda − ICMS − DIFAL)\n' +
                      'crédito sobre serviços = f × 9,25% × (taxa do marketplace + frete)\n' +
                      'PIS/COFINS efetivo     = da venda − crédito\n' +
                      'ajuste na margem       = PIS/COFINS da NF − efetivo\n\n' +
                      'f = 0,6 na Shopee e na Shein (60% de NF emitida) · 1 nos demais canais'}
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                    No recorte atual: a NF cobrou <b>R$ {brl0(somar(daView, 'pisNota'))}</b> de PIS/COFINS; o ajuste
                    gravado na coleção é de{' '}
                    <b>
                      {somar(daView, 'ajPis') >= 0 ? '+' : ''}R$ {brl0(somar(daView, 'ajPis'))}
                    </b>{' '}
                    na margem. As parcelas do regime e as marcas estão no bloco &ldquo;Da MC oficial à MC
                    ajustada&rdquo;, logo abaixo dos indicadores.
                  </Typography>
                  <Alert severity="info" sx={{ mb: 1 }}>
                    <b>Dois pontos a confirmar.</b> (1) “Valor da venda” está sendo lido como <code>order_total</code>,
                    a mesma receita que o resto do painel usa; na Shopee esse campo é o <b>preço cheio</b>, antes do{' '}
                    <code>seller_discount</code>. (2) O fator de 60% multiplica o parêntese inteiro, mas o{' '}
                    <b>ICMS e o DIFAL da base já vêm só das notas emitidas</b> — na Shopee eles são 5,9% da receita
                    contra 9,9% no Mercado Livre. Ou seja, o desconto de 40% acaba aplicado duas vezes sobre o imposto.
                    A alternativa seria <code>9,25% × (0,6 × venda − ICMS − DIFAL)</code>, que, na medição de 18/09/2026
                    (número fixo daquela data, não recalculado pela tela), dava R$ 2.737 a menos de margem ajustada em
                    Shopee + Shein.
                  </Alert>
                </Box>

                {/* conferência */}
                {raw.conf ? (
                  <Box>
                    <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 0.75 }}>
                      Conferência da base
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                      {raw.conf.venda.n === 0 && raw.conf.ad.n === 0 ? (
                        <>
                          A collection <code>margens</code> chegou a gravar o mesmo faturamento em três visões (
                          <code>type</code> = <code>pedido</code>, <code>venda</code> e <code>ad</code>), o que
                          triplicava qualquer soma. <b>As visões duplicadas foram removidas da base</b> — hoje só
                          resta <code>type:"pedido"</code>. O filtro continua aplicado por segurança.
                        </>
                      ) : (
                        <>
                          A collection <code>margens</code> grava <b>o mesmo faturamento em três visões</b>. Somar as
                          três triplica o resultado. Este painel usa somente a visão por pedido.
                        </>
                      )}
                    </Typography>
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell>Visão</TableCell>
                          <TableCell align="right">Registros</TableCell>
                          <TableCell align="right">Receita</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {(
                          [
                            ['type: "pedido" — a que este painel usa', raw.conf.pedido],
                            ['type: "venda"', raw.conf.venda],
                            ['type: "ad"', raw.conf.ad],
                          ] as const
                        ).map(([l, v]) => (
                          <TableRow key={l}>
                            <TableCell>{l}</TableCell>
                            <TableCell align="right">{brl0(v.n)}</TableCell>
                            <TableCell align="right">R$ {brl0(v.rev)}</TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                    {raw.conf.lag ? (
                      <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                        {raw.conf.lag}
                      </Typography>
                    ) : null}
                  </Box>
                ) : null}
              </Stack>
            </AccordionDetails>
          </Accordion>

        </Stack>
      ) : null}
    </DomainPageShell>
  )
}
